import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/Button";
import { formatTimeTo12Hour } from "../Oncall/utils";
import { isCapabilityEnabled } from "../../config/capabilities";
import { derivePlatformFromTrace } from "./validators/helper/trace";
import { getChartHistoryIsPrechartSummary } from "./chartHistoryUtils";
import { formatSmartChartYesNo } from "./smartChartUtils";
import SupportSection from "./SupportSection";
import CopyValueButton from "./CopyValueButton";
import { supportDoctorPathFromPatient } from "./supportUrls";
import {
  supportContextCardDoctor,
  supportContextCardPatient,
  supportContextCardTitle,
  supportInsetPanel,
  supportSubpanelHeader,
} from "./supportStyles";
import TranscriptAndNoteModal from "./TranscriptAndNoteModal";

const formatJsonFieldValue = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const ContextRow = ({ label, value, loading, action }) => {
  const displayValue =
    value === null || value === undefined || value === "" ? "—" : value;

  return (
  <div className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 items-start py-0.5">
    <dt className="text-[11px] text-gray-500 leading-snug pt-0.5">{label}</dt>
    <dd className="text-sm font-medium text-gray-900 break-words min-w-0 leading-snug">
      {loading ? (
        <span className="text-gray-400 font-normal">Loading…</span>
      ) : (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{displayValue}</span>
          {action}
        </span>
      )}
    </dd>
  </div>
  );
};

const CONTEXT_CARD_ACCENTS = {
  patient: supportContextCardPatient,
  doctor: supportContextCardDoctor,
};

const ContextBlock = ({ title, rows, ariaLabel, variant, footer }) => (
  <div
    className={`${supportInsetPanel} overflow-hidden ${CONTEXT_CARD_ACCENTS[variant] || ""}`}
    aria-label={ariaLabel}
  >
    <div className={supportSubpanelHeader}>
      <h4 className={supportContextCardTitle}>{title}</h4>
    </div>
    <dl className="px-3 py-2.5 space-y-0">{rows}</dl>
    {footer && <div className="px-3 pb-2.5">{footer}</div>}
  </div>
);

const PatientContextSection = ({
  patient,
  platform,
  platformLoading,
  smartChart,
  smartChartLoading,
  smartChartError,
}) => {
  const appointmentDate = patient?.date_of_appt?.trim() || null;
  const appointmentTime = patient?.appt_time?.trim()
    ? formatTimeTo12Hour(patient.appt_time)
    : null;
  const appointment =
    appointmentDate && appointmentTime
      ? `${appointmentDate} ${appointmentTime}`
      : appointmentDate || appointmentTime;

  const isPrechart = getChartHistoryIsPrechartSummary(patient?.chart_history);
  const smartChartEnabled = smartChart?.smart_chart === true;
  const smartChartUnavailable = Boolean(smartChartError) && !smartChartLoading;

  const rows = [
    { key: "name", label: "Name", value: patient.description },
    { key: "id", label: "Patient ID", value: patient.patient_id },
    { key: "mrn", label: "MRN", value: patient.mrn },
    { key: "patient-type", label: "Patient Type", value: patient.patient_type },
    { key: "is-prechart", label: "Is prechart", value: isPrechart },
    ...(isCapabilityEnabled("smartChart")
      ? [
          {
            key: "smart-chart",
            label: "Smart Chart",
            value: smartChartUnavailable
              ? "—"
              : smartChartLoading
                ? null
                : formatSmartChartYesNo(smartChartEnabled),
            loading: smartChartLoading,
          },
          {
            key: "additional-context",
            label: "Additional context",
            value: smartChartUnavailable
              ? "—"
              : smartChartLoading
                ? null
                : formatSmartChartYesNo(
                    smartChart?.has_additional_context === true
                  ),
            loading: smartChartLoading,
          },
          {
            key: "custom-prompt",
            label: "Custom Prompt",
            value: smartChartUnavailable
              ? "—"
              : smartChartLoading
                ? null
                : formatSmartChartYesNo(smartChart?.has_custom_prompt === true),
            loading: smartChartLoading,
          },
        ]
      : []),
    { key: "appointment", label: "Appointment", value: appointment },
    { key: "platform", label: "Platform", value: platform, loading: platformLoading },
    { key: "import-type", label: "Import type", value: patient.import_type },
    { key: "ehr-upload", label: "EHR upload status", value: patient.ehr_upload_status },
  ];

  return (
    <ContextBlock
      title="Patient"
      variant="patient"
      ariaLabel="Patient context"
      rows={rows.map((row) => (
        <ContextRow
          key={row.key}
          label={row.label}
          value={row.value}
          loading={row.loading}
        />
      ))}
    />
  );
};

const DoctorContextSection = ({ patient, doctor, doctorLoading }) => {
  const doctorName = patient.doctor_name || doctor?.user_name;
  const doctorSettingsPath = supportDoctorPathFromPatient(patient, doctor);

  const modelTypeChangeAction =
    doctorSettingsPath && !doctorLoading ? (
      <Link
        to={doctorSettingsPath}
        state={doctor ? { doctor } : undefined}
        className="text-sm font-medium text-ktx-purple hover:underline"
      >
        Change
      </Link>
    ) : null;

  const rows = [
    { key: "name", label: "Name", value: doctorName, loading: doctorLoading && !doctorName },
    {
      key: "org",
      label: "Organization",
      value: doctor?.organization,
      loading: doctorLoading && !doctor?.organization,
    },
    {
      key: "super-org",
      label: "Super org",
      value: doctor?.super_org,
      loading: doctorLoading && !doctor?.super_org,
    },
    ...(isCapabilityEnabled("oncoemr")
      ? [
          {
            key: "onco-emr-link",
            label: "Onco EMR Link",
            value: doctor?.onco_emr_url ? (
              <a
                href={doctor.onco_emr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ktx-purple underline break-all"
              >
                {doctor.onco_emr_url}
              </a>
            ) : null,
            loading: doctorLoading && !doctor?.onco_emr_url,
          },
        ]
      : []),
    {
      key: "specialty",
      label: "Specialty",
      value: doctor?.specialty,
      loading: doctorLoading && !doctor?.specialty,
    },
    {
      key: "model-type",
      label: "Model type",
      value: doctor?.model_type,
      loading: doctorLoading && !doctor?.model_type,
    },
    {
      key: "email",
      label: "Email",
      value: doctor?.user_email,
      loading: doctorLoading && !doctor?.user_email,
      action: <CopyValueButton value={doctor?.user_email} label="Copy email" />,
    },
    {
      key: "user-id",
      label: "User ID",
      value: doctor?.user_id || patient.user_id,
      loading: doctorLoading && !(doctor?.user_id || patient.user_id),
      action: (
        <CopyValueButton value={doctor?.user_id || patient.user_id} label="Copy user ID" />
      ),
    },
  ];

  return (
    <ContextBlock
      title="Doctor"
      variant="doctor"
      ariaLabel="Doctor context"
      rows={rows.map((row) => (
        <ContextRow
          key={row.key}
          label={row.label}
          value={row.value}
          loading={row.loading}
          action={row.key === "model-type" ? modelTypeChangeAction : row.action}
        />
      ))}
      footer={
        doctorSettingsPath && (
          <Link
            to={doctorSettingsPath}
            state={doctor ? { doctor } : undefined}
            className="text-sm font-medium text-ktx-purple hover:underline"
          >
            Open doctor page
          </Link>
        )
      }
    />
  );
};

const SummarySection = ({
  patient,
  doctor,
  doctorLoading,
  smartChart,
  smartChartLoading,
  smartChartError,
  trace,
  traceLoading,
  transcription,
  transcriptionLoading,
  transcriptionError,
  summarization,
  summarizationLoading,
  summarizationError,
  onPatientUpdate,
}) => {
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const patientSummaryText = formatJsonFieldValue(patient?.patient_summary);
  const hasPatientSummary = Boolean(patientSummaryText);

  const { platform, isLoading: platformLoading } = useMemo(
    () => derivePlatformFromTrace(trace, { isLoading: traceLoading }),
    [trace, traceLoading]
  );

  const doctorName = patient.doctor_name || doctor?.user_name;
  const collapsedSummary = [
    patient.description || "Patient",
    patient.mrn && `MRN ${patient.mrn}`,
    doctorName && `Dr. ${doctorName}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <SupportSection title="Summary" summary={collapsedSummary} defaultOpen>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <PatientContextSection
            patient={patient}
            platform={platform}
            platformLoading={platformLoading}
            smartChart={smartChart}
            smartChartLoading={smartChartLoading}
            smartChartError={smartChartError}
          />
          <DoctorContextSection
            patient={patient}
            doctor={doctor}
            doctorLoading={doctorLoading}
          />
        </div>

        {hasPatientSummary && (
          <div className="pt-3 border-t border-gray-200">
            <section>
              <p className="text-xs font-medium text-gray-600 mb-1.5">Patient summary</p>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-48 whitespace-pre-wrap text-gray-800">
                {patientSummaryText}
              </pre>
            </section>
          </div>
        )}

        <div className="pt-3 border-t border-gray-200 flex justify-end">
          <Button
            type="button"
            onClick={() => setTranscriptModalOpen(true)}
            className="bg-ktx-purple hover:bg-purple-700 text-white"
          >
            Review Note
          </Button>
        </div>
      </div>

      <TranscriptAndNoteModal
        open={transcriptModalOpen}
        onOpenChange={setTranscriptModalOpen}
        patient={patient}
        smartChart={smartChart}
        smartChartLoading={smartChartLoading}
        smartChartError={smartChartError}
        transcription={transcription}
        transcriptionLoading={transcriptionLoading}
        transcriptionError={transcriptionError}
        summarization={summarization}
        summarizationLoading={summarizationLoading}
        summarizationError={summarizationError}
        onPatientUpdate={onPatientUpdate}
      />
    </SupportSection>
  );
};

export default SummarySection;
